import { useRef, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { Button } from 'shared/ui'
import { useKeyGenerator } from 'shared/hooks'
import { type IAgentActionModel, type IAgentActionType } from 'entities/AgentActions'
import {
  type IAgentActionItem,
  type IAgentActionsConfig,
  type IAgentActionsControl,
  type IAgentActionsErrorsAdapter,
} from '../types/types'
import { ActionCard } from './ActionCard'
import { ActionsDropdown } from './ActionsDropdown'

import styles from './styles.module.scss'

type IAgentActionsListProps = {
  control: IAgentActionsControl
  errors: IAgentActionsErrorsAdapter
  actionItems: IAgentActionItem[]
  actionsConfig: IAgentActionsConfig
  addButtonText?: string
  emptyPlaceholder?: string
}

export const AgentActionsList = observer(
  ({
    control,
    errors,
    actionItems,
    actionsConfig,
    addButtonText = 'Add action',
    emptyPlaceholder,
  }: IAgentActionsListProps) => {
    const getActionKey = useKeyGenerator<IAgentActionModel>()
    const ref = useRef<HTMLButtonElement | null>(null)
    const [dropdownWidth, setDropdownWidth] = useState<number | undefined>()

    const handleAddAction = (type: IAgentActionType) => control.createAction(type)
    const handleDelete = (index: number, action: IAgentActionModel) => {
      control.removeAction(index)
      errors.removeActionError(action)
    }
    const handleToggleDropdown = () => {
      setDropdownWidth(ref.current?.getBoundingClientRect().width)
    }

    return (
      <div className={styles.container}>
        <div className={styles.list}>
          {control.list.length > 0
            ? control.list.map((action, index) => {
                const entry = actionsConfig[action.type]
                if (!entry) return null
                const { icon, title, Component } = entry
                const store = control.getActionStore(action)

                return (
                  <ActionCard
                    key={getActionKey(action)}
                    icon={icon}
                    title={title}
                    onDelete={() => handleDelete(index, action)}
                  >
                    <Component
                      control={store}
                      action={action}
                      error={errors.getActionError(action)}
                      onErrorReset={() => errors.removeActionError(action)}
                    />
                  </ActionCard>
                )
              })
            : emptyPlaceholder && <div className={styles.empty}>{emptyPlaceholder}</div>}
        </div>
        <ActionsDropdown
          dropdownWidth={dropdownWidth}
          handleToggleDropdown={handleToggleDropdown}
          handleAddAction={handleAddAction}
          items={actionItems}
          groupTitle='Action'
          triggerComponent={() => (
            <Button
              ref={ref}
              className={styles.addButton}
              icon='add'
              text={addButtonText}
              contained='secondary'
              size='medium'
            />
          )}
        />
      </div>
    )
  }
)
